/* 
 *   ____                                           _   
 *  |  _ \ _ __ ___  __ _ _ __ ___   __ _  ___  ___| |_ 
 *  | | | | '__/ _ \/ _` | '_ ` _ \ / _` |/ _ \/ __| __|
 *  | |_| | | |  __/ (_| | | | | | | (_| |  __/\__ \ |_ 
 *  |____/|_|  \___|\__,_|_| |_| |_|\__, |\___||___/\__|
 *                                  |___/     
 *           
 * Copyright 2021 Dreamgest ltd <help@dreamgest.com>.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* global Promise */

'use strict';
/**
 * Storage
 * 
 * Use cordova-sqlite-storage or localStorage on web debug
 * https://github.com/storesafe/cordova-sqlite-storage
 * 
 * #Intialize:
 * In app.js app -> onInit() {
 *    ...
 *    myStorage.f7 = $f7;
 *    myStorage.init();
 * }
 * 
 * 
 * #Create record:
 * myStorage.set(key, value)
 *  - key string required
 *  - value any required
 *  Return Promise
 *  
 *  Example:
 *  myStorage.set('user', user)
 *  .then(() => console.log('Storage success')
 *  .catch((e) => console.log('Storage error ' + e.message));
 *  
 *  
 * #Get record:
 * myStorage.get(key, json)
 *  - key string required
 *  - json bool optional -> excute parsing of value (JSON.parse(value))
 *  Return Promise resolve(value) or reject(error)
 *  
 *  If key do not exists return storage.defaultValue OR null if json = true
 *  
 *  Example:
 *  let user;
 *  myStorage.get('user', true)
 *  .then((value) => user = value)
 *  .catch((e) => console.log(e.message))
 *  
 *  OR in async function
 *  let user = await myStorage.get('user', true); // object
 *  let authToken = await myStorage.get('authToken'); // string
 *  
 * 
 * #Clear:
 * myStorage.clear();
 *  - delete all records
 *  Return Promise
 *  
 *  
 * #Direct access at db SQLite (only device.cordova):
 *  myStorage.db ...
 * https://github.com/storesafe/cordova-sqlite-storage
 * - myStorage.db.transaction( (tx) => tx.executeSql(string SQL), error(err), success )
 * - myStorage.db.executeSql( string SQL, array data, success(rs), error(err) )
 * 
 */
var storage = {
  db: null,
  f7: null,
  defaultValue: '',
  init: async () => {
    if (storage.f7.device.cordova) {
      storage.db = window.sqlitePlugin.openDatabase({name: 'my.db', location: 'default'});
      return new Promise((resolve, reject) => {
        storage.db.transaction((tx) => {
          tx.executeSql('CREATE TABLE IF NOT EXISTS myTable (myKey PRIMARY KEY, myValue)');
        }, (error) => {
          console.log(error, 'Table myTable KO');
          reject(error);
        }, () => {
          console.log('Table myTable Created');
          resolve();
        });
      });
    } else {
      return Promise.resolve();
    }
  },
  get: async (key, json, _table) => {
    return new Promise((resolve, reject) => {
      if (storage.f7.device.cordova) {
        let table = _table || 'myTable';
        storage.db.executeSql('SELECT myValue FROM ' + table + ' WHERE myKey = ?', [key], (rs) => {
          if (rs.rows.length) {
            let res = rs.rows.item(0).myValue;
            if (res && json) {
              resolve(JSON.parse(res));
            } else {
              resolve(res);
            }
          } else {
            resolve(json ? null : storage.defaultValue);
          }
        }, (error) => {
          console.log('myStoraqge.get() Error', error);
          reject(error);
        });
      } else {
        // debug //
        let d = localStorage.getItem(key);
        resolve(d && json ? JSON.parse(d) : d);
      }
    });
  },
  set: async (key, value, _table) => {
    let data = typeof value === 'object' || typeof value === 'boolean' ? JSON.stringify(value) : value;
    if (storage.f7.device.cordova) {
      return new Promise((resolve, reject) => {
        let table = _table || 'myTable';
        storage.db.transaction((tx) => {
          tx.executeSql('REPLACE INTO ' + table + ' VALUES (?,?)', [key, data]);
        }, (error) => {
          console.log('myStoraqge.set() Error', error);
          reject(error);
        }, () => {
          resolve(data);
        });
      });
    } else {
      localStorage.setItem(key, data);
      return Promise.resolve(data);
    }
  },
  deleteItem: async (key, _table) => {
    if (storage.f7.device.cordova) {
      return new Promise((resolve, reject) => {
        let table = _table || 'myTable';
        storage.db.transaction((tx) => {
          tx.executeSql('DELETE FROM ' + table + ' WHERE myKey = ?', [key]);
        }, (error) => {
          console.log('myStoraqge.deleteItem() Error', error);
          resolve(false);
        }, () => {
          resolve(true);
        });
      });
    } else {
      localStorage.removeItem(key);
    }
  },
  clear: async () => {
    if (storage.f7.device.cordova) {
      return new Promise((resolve, reject) => {
        storage.db.transaction((tx) => {
          tx.executeSql('DELETE FROM myTable');
        }, (error) => {
          console.log('myStoraqge.clear() Error', error);
          reject(error);
        }, () => {
          console.log('myStoraqge.clear() OK');
          resolve();
        });
      });
    } else {
      localStorage.clear();
      return Promise.resolve();
    }
  },
  indexedDB: () => {
    // quite untested, adapted from BigstickCarpet's gist, attempt to make it simpler to use

    function openIndexedDB(fileindex) {
      // This works on all devices/browsers, and uses IndexedDBShim as a final fallback 
      var indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB || window.shimIndexedDB;
      var openDB = indexedDB.open("MyDatabase", 1);
      openDB.onupgradeneeded = function () {
        var db = {}
        db.result = openDB.result;
        db.store = db.result.createObjectStore("MyObjectStore", {keyPath: "id"});
        if (fileindex) db.index = db.store.createIndex("NameIndex", fileindex);
      };
      return openDB;
    }

    function getStoreIndexedDB(openDB) {
      var db = {};
      db.result = openDB.result;
      db.tx = db.result.transaction("MyObjectStore", "readwrite");
      db.store = db.tx.objectStore("MyObjectStore");
      db.index = db.store.index("NameIndex");
      return db;
    }

    function saveIndexedDB(filename, filedata, fileindex) {
      return new Promise((resolve, reject) => {
        var openDB = openIndexedDB(fileindex);
        openDB.onsuccess = function () {
          var db = getStoreIndexedDB(openDB);
          db.store.put({id: filename, data: filedata});
          resolve();
        }
        openDB.onerror = () => reject('Save error');
      })


    }

    function findIndexedDB(filesearch, callback) {
      return loadIndexedDB(null, callback, filesearch);
    }

    function loadIndexedDB(filename, callback, filesearch) {
      var openDB = openIndexedDB();
      openDB.onsuccess = function () {
        var db = getStoreIndexedDB(openDB);
        var getData;
        if (filename) {
          getData = db.store.get(filename);
        } else {
          getData = db.index.get(filesearch);
        }

        getData.onsuccess = function () {
          callback(getData.result.data);
        };
        db.tx.oncomplete = function () {
          db.result.close();
        };
      }

      return true;
    }

    this.get = async (id) => {
      return new Promise((resolve, reject) => {
        //const callback = (res) => resolve(res);
        loadIndexedDB(12345, resolve);
      });
    }

    this.set = async (id, data) => {
      saveIndexedDB(id, data);
    }



  }
}

export default storage;